import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Checkbox,
  Field,
  FieldTypes,
  Form,
  FormSizes,
  Typography,
  TypographyVariants,
  TypographyColors,
  TextInput,
  FormValidateOn,
} from '@carboledger/ui';
import { COUNTRY_CODES } from '@/config/countries.constants';
import { buildCountryOptions, isFormDirty } from '@/lib/utils';
import { ConfirmDialog } from '@/components/feedback';
import {
  AllocationPreferenceField,
  ReferenceIdField,
  FacilityField,
  MaterialField,
  SoldToField,
  ShippingToField,
  DateOfDispatchField,
  RawMaterialTypeField,
  CountryOfOriginField,
  FeedstockCategoryField,
} from '@/components/common';
import { useTranslations } from '@/hooks';
import {
  buildAllocationPreferenceOptions,
  buildFeedstockCategoryOptions,
} from '@/config/formOptions';
import { orderValidation } from '../ordersValidation';
import { OrderQuantityFields } from './OrderQuantityFields';
import { OrderTerminalField } from './OrderTerminalField';
import styles from './OrderForm.module.scss';

// facility → terminal → material → unit. Picking a parent drops every stale descendant.
const FACILITY_CLEARS = Object.freeze(['terminalId', 'materialId', 'unit']);
const MATERIAL_CLEARS = Object.freeze(['unit', 'density']);

// Every field the form owns, so a missing key can't become an uncontrolled input.
export const EMPTY_ORDER_FORM = Object.freeze({
  idPrefix: '',
  idNumber: '',
  facilityId: '',
  terminalId: '',
  materialId: '',
  soldToId: '',
  shippingToId: '',
  tolerance: '',
  dateOfDispatch: '',
  contractedQuantity: '',
  unit: '',
  density: '',
  // Not a control — the chosen material's display_unit row, which the payload needs.
  displayUnitId: '',
  ghgEmissions: '',
  ghgEmissionSavings: '',
  rawMaterialType: '',
  countryOfOrigin: '',
  feedstockCategory: '',
  allocationPreference: [],
  mandatoryMaxGhgEmission: false,
  mandatoryMinGhgSaving: false,
  mandatoryRawMaterialType: false,
  mandatoryCountryOfOrigin: false,
});

// One "Mark as mandatory" checkbox, paired beneath a Preferences field.
const MandatoryCheckbox = ({ name, disabled }) => {
  const t = useTranslations();
  return (
    <Field name={name} type={FieldTypes.checkbox}>
      {({ id, name: fieldName, checked, onChange }) => (
        <Checkbox
          id={id}
          name={fieldName}
          label={t('ORDERS_FORM_MARK_AS_MANDATORY')}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   title · submitLabel · initialValues · onSubmit · onClose · isSubmitting
 *   isEdit          locks the order no — a saved order keeps the one it was given
 *   isLocked        something is shipped or allocated: every field but the quantity is locked
 *   options         { facilities, customers, rawMaterialTypes }
 *   sites           site view models, for a facility's terminals
 *   materials       material view models, for units and the quantity conversion
 *   shippedByUnit   unit symbol → already-shipped quantity, the quantity's floor
 *   allocatedByUnit unit symbol → already-allocated quantity, the quantity's floor
 */
export const OrderForm = ({
  title,
  submitLabel,
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  isEdit = false,
  isLocked = false,
  options,
  sites,
  materials,
  shippedByUnit = null,
  allocatedByUnit = null,
}) => {
  const t = useTranslations();

  const feedstockCategoryOptions = useMemo(() => buildFeedstockCategoryOptions(t), [t]);
  const allocationPreferenceOptions = useMemo(() => buildAllocationPreferenceOptions(t), [t]);
  const countryOptions = useMemo(() => buildCountryOptions(COUNTRY_CODES), []);

  const [confirmingClose, setConfirmingClose] = useState(false);
  const askBeforeClosing = useCallback(() => setConfirmingClose(true), []);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);

  return (
    <Form
      validateOn={FormValidateOn.change}
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values }) => {
        const requestClose = isFormDirty(values, initialValues) ? askBeforeClosing : onClose;
        return (
          <>
            <Form.Header title={title} onClose={requestClose} closeLabel={t('COMMON_CLOSE')} />

            <Form.Section>
              {isLocked ? (
                <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
                  {t('ORDERS_FORM_REQUIRED_FIELDS_NOTE')}
                </Typography>
              ) : null}

              <Form.Row columns={4}>
                <div className={styles.span2}>
                  <ReferenceIdField
                    label={t('ORDERS_COL_ORDER_NO')}
                    prefixLabel={t('ORDERS_FORM_ORDER_ID_PREFIX')}
                    numberLabel={t('ORDERS_FORM_ORDER_ID_NUMBER')}
                    readOnly={isEdit}
                    rules={orderValidation.idNumber}
                  />
                </div>

                <FacilityField
                  label={t('FIELD_FACILITY_NAME')}
                  options={options.facilities}
                  clears={FACILITY_CLEARS}
                  disabled={isLocked}
                />

                <OrderTerminalField sites={sites} disabled={isLocked} />
              </Form.Row>

              <Form.Row columns={4}>
                <div className={styles.span2}>
                  <MaterialField
                    materials={materials}
                    certificateType={options.certificateType}
                    clears={MATERIAL_CLEARS}
                    disabled={isLocked}
                  />
                </div>

                <SoldToField options={options.customers} disabled={isLocked} />

                <ShippingToField disabled={isLocked} />
              </Form.Row>

              <OrderQuantityFields
                materials={materials}
                isLocked={isLocked}
                shippedByUnit={shippedByUnit}
                allocatedByUnit={allocatedByUnit}
              >
                <Field name="tolerance" rules={orderValidation.tolerance}>
                  {({ id, name, value, onChange, onBlur, status, message }) => (
                    <TextInput
                      id={id}
                      name={name}
                      label={t('ORDERS_FORM_TOLERANCE_OPTIONAL')}
                      placeholder={t('ORDERS_FORM_TOLERANCE_PLACEHOLDER')}
                      suffix="%"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      status={status}
                      message={message}
                      disabled={isLocked}
                    />
                  )}
                </Field>

                <DateOfDispatchField label={t('ORDERS_FORM_DISPATCH_BY')} disabled={isLocked} />
              </OrderQuantityFields>
            </Form.Section>

            <Form.Section title={t('ORDERS_FORM_PREFERENCES_TITLE')} className={styles.body}>
              <Form.Row columns={1}>
                <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
                  {t('ORDERS_FORM_PREFERENCES_DESCRIPTION')}
                </Typography>
              </Form.Row>

              <Form.Row>
                <div className={styles.preferenceField}>
                  <Field name="ghgEmissions" rules={orderValidation.ghgEmissions}>
                    {({ id, name, value, onChange, onBlur, status, message }) => (
                      <TextInput
                        id={id}
                        name={name}
                        label={t('ORDERS_FORM_MAX_GHG_EMISSIONS')}
                        placeholder={t('ORDERS_FORM_MAX_GHG_EMISSIONS_PLACEHOLDER')}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        status={status}
                        message={message}
                        disabled={isLocked}
                      />
                    )}
                  </Field>
                  <MandatoryCheckbox name="mandatoryMaxGhgEmission" disabled={isLocked} />
                </div>

                <div className={styles.preferenceField}>
                  <Field name="ghgEmissionSavings" rules={orderValidation.ghgEmissionSavings}>
                    {({ id, name, value, onChange, onBlur, status, message }) => (
                      <TextInput
                        id={id}
                        name={name}
                        label={t('ORDERS_FORM_MIN_GHG_SAVINGS')}
                        placeholder={t('ORDERS_FORM_MIN_GHG_SAVINGS_PLACEHOLDER')}
                        suffix="%"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        status={status}
                        message={message}
                        disabled={isLocked}
                      />
                    )}
                  </Field>
                  <MandatoryCheckbox name="mandatoryMinGhgSaving" disabled={isLocked} />
                </div>

                <div className={styles.preferenceField}>
                  <RawMaterialTypeField options={options.rawMaterialTypes} disabled={isLocked} />
                  <MandatoryCheckbox name="mandatoryRawMaterialType" disabled={isLocked} />
                </div>

                <div className={styles.preferenceField}>
                  <CountryOfOriginField options={countryOptions} disabled={isLocked} />
                  <MandatoryCheckbox name="mandatoryCountryOfOrigin" disabled={isLocked} />
                </div>
              </Form.Row>

              <Form.Row>
                <FeedstockCategoryField options={feedstockCategoryOptions} disabled={isLocked} />

                <AllocationPreferenceField
                  options={allocationPreferenceOptions}
                  disabled={isLocked}
                />
              </Form.Row>
            </Form.Section>

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting}
              >
                {submitLabel}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingClose}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={onClose}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};
